import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { Card, Btn, Spinner, cellClip } from "./Shared";
import { fmtBytes } from "../wizard/SubmissionWizard";

/**
 * What is actually in the bucket, and a way to get one file out of it.
 *
 * The API for this existed for months with no caller here at all, and was
 * operator-only — so a researcher could not fetch their own delivered files
 * unless somebody sent them a link. The server confines each reader to what
 * they may see (the whole project folder for an operator, the delivery area for
 * a researcher); this only renders what it is given.
 */
const th = { padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 600,
             color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "8px 12px", fontSize: 13, color: "#374151", ...cellClip };

export default function StorageBrowser({ project }) {
  const [prefix, setPrefix]   = useState(null);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(p => {
    setLoading(true);
    setError(null);
    api.get(`/api/projects/${project.id}/s3-browse`
            + (p ? `?prefix=${encodeURIComponent(p)}` : ""))
      // Normalised on the way in. A response missing a field would otherwise
      // throw during render, and React's answer to a render error is to unmount
      // the whole application — a blank page with no message, which is how the
      // Settings tab once disappeared entirely over one undefined prop.
      .then(r => {
        setData({ bucket: r?.bucket || "", root: r?.root || "", prefix: r?.prefix || "",
                  folders: r?.folders || [], objects: r?.objects || [] });
        setPrefix(r?.prefix || "");
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(null); }, [load]);

  const download = async key => {
    setError(null);
    try {
      const { url } = await api.get(
        `/api/projects/${project.id}/s3-object/download-link?key=${encodeURIComponent(key)}`);
      // A plain navigation, so the browser streams it to disk itself rather than
      // the page holding the whole file in memory. The link carries its own
      // short-lived token because a navigation cannot send a header.
      window.location.href = url;
    } catch (e) {
      setError(e.message);
    }
  };

  const up = () => {
    const root = (data?.root || "").replace(/\/$/, "");
    const here = (prefix || "").replace(/\/$/, "");
    if (here === root) return;
    load(here.slice(0, here.lastIndexOf("/")) + "/");
  };

  if (error) return <Card><div role="alert" style={{ color: "#dc2626" }}>{error}</div></Card>;
  if (!data) return <Card><Spinner /> Reading storage…</Card>;

  const atRoot = (prefix || "").replace(/\/$/, "") === (data.root || "").replace(/\/$/, "");

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Files in storage</h3>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "#6b7280",
                  fontFamily: "'JetBrains Mono', monospace", overflowWrap: "anywhere" }}>
        s3://{data.bucket}/{prefix}
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Btn variant="secondary" onClick={up} disabled={atRoot || loading}>Up</Btn>
        <Btn variant="secondary" onClick={() => load(null)} disabled={loading}>Top</Btn>
        {loading && <span style={{ fontSize: 12, color: "#6b7280" }}><Spinner /> Loading…</span>}
      </div>

      {data.folders.length === 0 && data.objects.length === 0 ? (
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>This folder is empty.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="responsive-table"
                 style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <th style={{ ...th, width: "60%" }}>Name</th>
                <th style={{ ...th, width: "20%" }}>Size</th>
                <th style={{ ...th, width: "20%" }} />
              </tr>
            </thead>
            <tbody>
              {data.folders.map(f => {
                const name = f.replace(prefix, "").replace(/\/$/, "");
                return (
                  <tr key={f} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={td} data-label="Name" title={name}>
                      <button
                        onClick={() => load(f)}
                        style={{ background: "none", border: "none", padding: 0, font: "inherit",
                                 color: "#2563eb", cursor: "pointer", textAlign: "left" }}
                      >📁 {name}</button>
                    </td>
                    <td style={td} data-label="Size" />
                    <td style={td} />
                  </tr>
                );
              })}
              {data.objects.map(o => (
                <tr key={o.key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={td} data-label="Name" title={o.name}>{o.name}</td>
                  <td style={td} data-label="Size">{fmtBytes(o.size)}</td>
                  <td style={td} data-label="">
                    <Btn variant="secondary" onClick={() => download(o.key)}>Download</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
